import Vue from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'

// Типы для элементов дерева
interface TreeNode {
  id: string
  name: string
  children?: TreeNode[]
}

// Типы для мест (places)
interface PlacesShow {
  location: string[]
  service: string[]
  cabinet: string[]
  device: string[]
}

interface PlacesCreate {
  location: string[]
  cabinet: string[]
  device: string[]
}

interface PlacesUpdate {
  location: string[]
  cabinet: string[]
  device: string[]
}

interface Places {
  show: PlacesShow
  create: PlacesCreate
  update: PlacesUpdate
}

// Типы для UI
interface UICommon {
  dashboard: boolean
  topology: boolean
  settings: boolean
  reports: boolean
}

interface UILogical {
  channel: boolean
  phone: boolean
  ip: boolean
  VLAN: boolean
  vm: boolean
}

interface UICmdb {
  dashboard: boolean
  configuration: boolean
  reports: boolean
  physical: boolean
  logical: UILogical
  ref_book: boolean
  audit: boolean
  seeking: boolean
  users: boolean
}

interface UIMonitoring {
  o_journal: boolean
  arch_journal: boolean
  mnemo: boolean
  topology: boolean
}

interface UIInforming {
  sms: boolean
  email: boolean
}

interface UI {
  common: UICommon
  cmdb: UICmdb
  monitoring: UIMonitoring
  informing: UIInforming
  [key: string]: any
}

// Типы для состояния
interface CurrentState {
  menu: boolean
  module: string
  tab: string | null
}

interface UserState {
  menu: boolean
  fav: boolean
  message: boolean
  hints: boolean
}

interface TreeState {
  treeActive: any[]
  treeOpened: any[]
  tree: TreeNode[]
}

// Базовая структура (то, что сбрасывается)
interface BaseStructureState {
  kiosk: boolean
  modules: string[]
  pages: Record<string, string[]>
  user: UserState
  current: CurrentState
  manage: TreeState
  settings: TreeState
  places: Places
  placesDict: Record<string, string>
}

// Полное состояние с persistent-свойствами
interface StructureState extends BaseStructureState {
  license: any | null
  uiLoaded: boolean
  interfaceLoaded: boolean
  ui: UI
}

// Начальное состояние для сбрасываемой части
const getDefaultBaseState = (): BaseStructureState => {
  return {
    kiosk: false,
    modules: ['common'],
    pages: {
      common: [
        'dashboard',
        'objects',
        'topology',
        'settings',
      ],
      cmdb: [
        'dashboard',
        'objects',
        'topology',
        'configuration',
      ],
    },
    user: {
      menu: false,
      fav: true,
      message: false,
      hints: true,
    },
    current: {
      menu: false,
      module: 'common',
      tab: null,
    },
    manage: {
      treeActive: [],
      treeOpened: [],
      tree: [
        { 
          id: 'common',
          name: 'common',
          children: [
            { 
              id: 'time_synchronization',
              name: 'time synchronization',
              children: [
                { 
                  id: 'time_synchronization_sntp_authentication',
                  name: 'sntp authentication',
                },
                {
                  id: 'time_synchronization_clock',
                  name: 'clock',
                }
              ]
            },
            {
              id: 'snmp',
              name: 'snmp',
            },
            { 
              id: 'maintenance',
              name: 'maintenance',
              children: [
                { 
                  id: 'maintenance_file_management',
                  name: 'file management',
                  children: [
                    { 
                      id: 'maintenance_file_management_image',
                      name: 'image',
                    },
                    { 
                      id: 'maintenance_file_management_configuration',
                      name: 'configuration',
                    },
                  ]
                },
                { 
                  id: 'maintenance_user_accounts',
                  name: 'user accounts',
                },
              ]
            },
            {
              id: 'stack',
              name: 'stack',
            },
            {
              id: 'general_configuration',
              name: 'general configuration',
            },
            { 
              id: 'syslog',
              name: 'syslog',
            },
          ]
        },
        { 
          id: 'switching',
          name: 'switching',
          children: [
            {
              id: 'ARP',
              name: 'ARP',
            },
            { 
              id: 'port_configuration',
              name: 'port configuration',
            },
            {
              id: 'POE',
              name: 'POE',
            },
            { 
              id: 'port_security',
              name: 'port security',
            },
            { 
              id: 'VLAN',
              name: 'VLAN',
              children: [
                { 
                  id: 'VLAN_configuration',
                  name: 'configuration',
                },
                {
                  id: 'VLAN_port_membership',
                  name: 'port membership',
                },
                {
                  id: 'VLAN_port_setting',
                  name: 'port setting',
                },
                {
                  id: 'VLAN_LAG_setting',
                  name: 'LAG setting',
                },
                {
                  id: 'VLAN_voice_VLAN',
                  name: 'voice VLAN',
                }
              ]
            },
            {
              id: 'LLDP',
              name: 'LLDP',
              children: [
                { 
                  id: 'lldp_configuration',
                  name: 'configuration',
                },
                {
                  id: 'lldp_med',
                  name: 'lldp med',
                },
              ]
            },
            {
              id: 'acl',
              name: 'access control list',
              children: [
                { 
                  id: 'acl_ip',
                  name: 'ip',
                }
              ]
            },
          ]
        },
        { 
          id: 'routing',
          name: 'routing',
          children: [
            { 
              id: 'routing_IP',
              name: 'IP',
            }
          ]
        },
        {
          id: 'quality_of_service',
          name: 'quality of service',
        }
      ],
    },
    settings: {
      treeActive: [],
      treeOpened: [],
      tree: [
        {
          id: 'license',
          name: 'license',
        },
        { 
          id: 'users',
          name: 'users',
        },      
        { 
          id: 'auth',
          name: 'auth',
          children: [
            { 
              id: 'ldap',
              name: 'LDAP',
            },
            { 
              id: 'keycloak',
              name: 'KEYCLOAK',
            },
          ]
        },
        { 
          id: 'notifications',
          name: 'notifications',
          children: [
            { 
              id: 'email',
              name: 'email',
            },
            { 
              id: 'sms',
              name: 'sms',
            },
            { 
              id: 'push',
              name: 'push notifications',
            },
          ]
        },
        { 
          id: 'maintenance',
          name: 'maintenance',
          children: [
            {
              id: 'sessions',
              name: 'sessions',
              children: [
                {
                  id: 'current_sessions',
                  name: 'current sessions',
                },{
                  id: 'session_settings',
                  name: 'settings',
                },
              ]
            },
            {
              id: 'backup_management',
              name: 'backup management',
            },
            {
              id: 'service_status',
              name: 'component status',
            },
            {
              id: 'sertificates',
              name: 'sertificates',
            },
            {
              id: 'self_diagnosis',
              name: 'self-diagnosis',
            },  
          ]
        },
        { 
          id: 'ztp',
          name: 'ztp',
          children: [
            { 
              id: 'ztp_configuration',
              name: 'configuration',
            },
            { 
              id: 'ztp_templates',
              name: 'templates',
            },
          ]
        },
        { 
          id: 'editor',
          name: 'editor',
          children: [
            { 
              id: 'panels',
              name: 'panels',
            },
            { 
              id: 'tables',
              name: 'tables',
            },
            { 
              id: 'templates',
              name: 'templates',
            },
          ]
        },
        { 
          id: 'books',
          name: 'books',
          children: [
            { 
              id: 'organization_book',
              name: 'organization',
            },
            { 
              id: 'project_book',
              name: 'contract',
            },
            // { 
            //   id: 'person_book',
            //   name: 'person',
            // },
            // { 
            //   id: 'license_book',
            //   name: 'license',
            // },
          ]
        },
      ],
    },
    places: {
      show: {
        location: ['composition', 'main', 'graph', 'services'],
        service: ['composition', 'dashboard', 'params', 'settings', 'configuration'],
        cabinet: ['composition', 'main', 'service', 'events', 'services'],
        device: ['monitoring', 'console', 'manage'],
      },
      create: {
        location: [],
        cabinet: [],
        device: [],
      },
      update: {
        location: ['main'],
        cabinet: ['main', 'photo', 'settings'],
        device: ['main', 'params', 'settings'],
      }
    },
    placesDict: {
      'event archive': 'arch_journal',
      'events journal': 'events_journal',
      audit: 'audit',
      cmdb: 'cmdb',
      logical: 'logical',
      channel: 'logical_channel',
      crossconnection: 'crossconnection',
      'ip-address': 'logical_ip',
      phone: 'logical_phone',
      logical_VLAN: 'VLAN',
      logical_vm: 'logical_vm',
      phone_range: 'phone_range',
      phone_extension: 'phone_extension',
      path_resource: 'path_resource',
      connection_resource: 'connection_resource',
      'ip-network': 'ip-network',
      'mimic diagrams': 'mnemo',
      monitoring: 'monitoring',
      'register log': 'o_journal',
      'journal': 'n_journal',
      physical: 'physical',
      books: 'ref_book',
      seeking: 'seeking',
      topology: 'topology',
      users: 'users',
    },
  }
}

// Persistent-свойства (не сбрасываются)
const getPersistentState = () => {
  return {
    license: null,
    uiLoaded: false,
    interfaceLoaded: false,
    ui: {
      common: {
        dashboard: true,
        topology: true,
        settings: true,
        reports: true
      },
      cmdb: {
        dashboard: true,
        configuration: true,
        reports: true,
        physical: true,
        logical: {
          channel: true,
          phone: true,
          ip: false,
          VLAN: false,
          vm: false
        },
        ref_book: true,
        audit: true,
        seeking: true,
        users: true
      },
      monitoring: {
        o_journal: true,
        arch_journal: true,
        mnemo: true,
        topology: true
      },
      informing: {
        sms: true,
        email: true
      },
    },
  }
}

// Полное начальное состояние
const getDefaultState = (): StructureState => {
  return {
    ...getDefaultBaseState(),
    ...getPersistentState(),
  }
}

const state: StructureState = getDefaultState()

export default {
  state,

  mutations: {
    RESET_STRUCTURE_STATE(state: StructureState) {
      // Сбрасываем только базовую часть, persistent-свойства сохраняем
      const defaultBaseState = getDefaultBaseState()
      Object.keys(defaultBaseState).forEach(key => {
        Vue.set(state, key, (defaultBaseState as any)[key])
      })
    },

    KIOSK(state: StructureState, data?: boolean) {
      state.kiosk = data || false
    },
    
    MODULE_CHANGE(state: StructureState, data: string) {
      Vue.set(state.current, 'module', data)
    },

    PAGE_CHANGE(state: StructureState, data: string) {
      Vue.set(state.current, 'tab', data)
    },

    SET_UI(state: StructureState, data: UI) {
      if(data) {
        Vue.set(state, 'ui', data)
        Vue.set(state, 'uiLoaded', true)
        console.log("SET_UI", state.ui)
      }
    },

    SET_LICENSE(state: StructureState, data: any) {
      if(data) {
        Vue.set(state, 'license', data)
        console.log("LIC", state.license)
      }
    },

    SET_INTERFACE_LOADED(state: StructureState, data: boolean) {
      if(data !== undefined) {
        Vue.set(state, 'interfaceLoaded', data)
      }
    },
  },

  actions: {
    async resetStructureState({ commit }: any) {
      commit('RESET_STRUCTURE_STATE')
    },

    moduleChange({ state, commit, dispatch }: any, module: string) {
      if(!module) return
      commit('MODULE_CHANGE', module)
      dispatch('saveModule', module, { root: true })
    },

    pageChange({ state, commit, dispatch }: any, page: string) {
      console.log("STORE", 'STRUCTURE_JS', 'pageChange STARTED', page)
      if(!page) return
      commit('PAGE_CHANGE', page)
      dispatch('savePage', page, { root: true })
    },

    async loadLicense({ commit }: any, data?: any) {
      try {
        const lic = await $_bridge.customQuery('cmdb', 'lic', data, 'get')
        if(lic) {
          commit('SET_LICENSE', lic)
        }
        if(lic?.ui) {
          console.log("LIC UI", lic.ui)
          const p: Record<string, any> = {}
          
          Object.keys(lic.ui).forEach((key) => {
            if(Object.keys(lic.ui[key]).length) {
              p[key] = true
              Object.keys(lic.ui[key]).forEach((item) => {
                p[item] = lic.ui[key][item]
                if(item.substring(0, 7) === 'logical' && lic.ui[key][item] === true) {
                  p.logical = true
                }
              })
            } else if(!!lic.ui[key]) {
              p[key] = true
            }
          })

          p.configuration = true
          p.objects = true
          p.services = true
          p.settings = true
          p.dashboard = true
          p.reports = true
          p.topology = true
          p.physical = true

          console.log(">>>>", p)
          commit('SET_UI', p)
        } else {
          alert('load license false')
        }
      } catch(err) {
        alert('load license false ' + err)
      }
    },
  },

  getters: {
    kiosk: (state: StructureState): boolean => {
      return state.kiosk
    },
    
    getPlaces: (state: StructureState): Places => {
      return state.places
    },

    getUI: (state: StructureState): UI => {
      return state.ui
    },

    getUILoaded: (state: StructureState): boolean => {
      return state.uiLoaded
    },

    getInterfacLoaded: (state: StructureState): boolean => {
      return state.interfaceLoaded
    },

    getLicense: (state: StructureState): any => {
      return state.license
    },

    checkPage: (state: StructureState) => (page: string): boolean => {
      const page$ = state.placesDict[page] || page
      return state.ui[page$] === true
    },

    getCurrentPosition: (state: StructureState): CurrentState => {
      return state.current
    },

    getModules: (state: StructureState): string[] => {
      return state.modules.filter(module => {
        const module$ = state.placesDict[module] || module
        return state.ui[module$] === true
      })
    },

    getPages: (state: StructureState): string[] => {
      return state.pages[state.current.module]?.filter(page => {
        const page$ = state.placesDict[page] || page
        return state.ui[page$] === true
      }) || []
    },

    getManageTreeActive: (state: StructureState): any[] => {
      return state.manage.treeActive
    },

    getManageTreeOpened: (state: StructureState): any[] => {
      return state.manage.treeOpened
    },

    getSettingsTreeActive: (state: StructureState): any[] => {
      return state.settings.treeActive
    },

    getSettingsTreeOpened: (state: StructureState): any[] => {
      return state.settings.treeOpened
    },
  }
}